import React, { useCallback, useContext, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { v4 as uuidV4 } from 'uuid';
import type { HubOptionScore as HubOptionScoreType } from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO, MemoryType } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { NodeEditContext } from '../../../context/NodeEditContext';
import { HubValueSelect } from './HubValueSelect';
import { HubClearButton } from '../HubClearButton/HubClearButton';
import './HubOptionScore.scss';

interface HubOptionScoreProps {
  score: HubOptionScoreType | null;
  isDisabled: boolean;
  onChange: (score: HubOptionScoreType | null) => void;
}

type SideKey = 'numerator' | 'denominator';
type SideChange = { intValue?: string; varValue?: number };

function emptyScore(): HubOptionScoreType {
  return {
    uuid: uuidV4(),
    numeratorUuid: uuidV4(),
    denominatorUuid: uuidV4(),
    numeratorType: 'literal',
    numeratorLiteral: null,
    numeratorVariableId: null,
    denominatorType: 'literal',
    denominatorLiteral: null,
    denominatorVariableId: null,
  };
}

function applySideChange(
  prev: HubOptionScoreType,
  data: SideChange,
  side: SideKey,
): HubOptionScoreType {
  const typeKey = `${side}Type` as const;
  const literalKey = `${side}Literal` as const;
  const varKey = `${side}VariableId` as const;

  if (data.varValue !== undefined) {
    const variableId = data.varValue === 0 ? null : data.varValue;
    return {
      ...prev,
      [typeKey]: 'variable',
      [literalKey]: null,
      [varKey]: variableId,
    };
  }
  if (data.intValue !== undefined) {
    return {
      ...prev,
      [typeKey]: 'literal',
      [literalKey]: data.intValue,
      [varKey]: null,
    };
  }
  return prev;
}

export function HubOptionScore({ score, isDisabled, onChange }: HubOptionScoreProps) {
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const ctxMemoryBankSlots = ctx?.memoryBankSlots as MemoryDTO[] | undefined;

  const numericVariables = useMemo(
    () => (ctxMemoryBankSlots ?? []).filter((slot) => slot.type === MemoryType.Number),
    [ctxMemoryBankSlots],
  );

  const current = useMemo(() => score ?? emptyScore(), [score]);

  const handleSideChange = useCallback(
    (data: SideChange, side: SideKey) => {
      onChange(applySideChange(current, data, side));
    },
    [current, onChange],
  );

  const handleNumeratorChange = useCallback(
    (data: SideChange) => handleSideChange(data, 'numerator'),
    [handleSideChange],
  );

  const handleDenominatorChange = useCallback(
    (data: SideChange) => handleSideChange(data, 'denominator'),
    [handleSideChange],
  );

  const handleClear = useCallback(() => onChange(null), [onChange]);

  const numIntValue = current.numeratorType === 'literal'
    ? current.numeratorLiteral ?? undefined : undefined;
  const numVarValue = current.numeratorType === 'variable'
    ? current.numeratorVariableId ?? undefined : undefined;
  const denIntValue = current.denominatorType === 'literal'
    ? current.denominatorLiteral ?? undefined : undefined;
  const denVarValue = current.denominatorType === 'variable'
    ? current.denominatorVariableId ?? undefined : undefined;

  const excludeForNumerator = current.denominatorType === 'variable'
    ? current.denominatorVariableId ?? undefined : undefined;
  const excludeForDenominator = current.numeratorType === 'variable'
    ? current.numeratorVariableId ?? undefined : undefined;

  const hasAnyValue = score != null && (
    numIntValue !== undefined || numVarValue !== undefined
    || denIntValue !== undefined || denVarValue !== undefined
  );

  return (
    <div className="hub-option-score d-flex align-items-center" data-testid="hub-option-score">
      <Form.Group className="hub-option-score__side mb-0 mr-2">
        <HubValueSelect
          subConditionUuid={current.numeratorUuid}
          intValue={numIntValue}
          varValue={numVarValue}
          variables={numericVariables}
          excludeVariableId={excludeForNumerator}
          onChange={handleNumeratorChange}
          disabled={isDisabled}
          placeholder="Numerator..."
        />
      </Form.Group>
      <span className="hub-option-score__separator mr-2" aria-hidden="true">/</span>
      <Form.Group className="hub-option-score__side mb-0 mr-2">
        <HubValueSelect
          subConditionUuid={current.denominatorUuid}
          intValue={denIntValue}
          varValue={denVarValue}
          variables={numericVariables}
          excludeVariableId={excludeForDenominator}
          onChange={handleDenominatorChange}
          disabled={isDisabled}
          placeholder="Denominator..."
        />
      </Form.Group>
      {hasAnyValue && (
        <HubClearButton
          onClick={handleClear}
          disabled={isDisabled}
          title="Clear score"
        />
      )}
    </div>
  );
}
